import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Image, Upload, Clock, Bell, Briefcase, Send, MessageCircle, Cloud, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '@/utils/api';

const SiteSettingsCMS = () => {
  const [heroBanner, setHeroBanner] = useState('');
  const [newBannerUrl, setNewBannerUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scheduleSettings, setScheduleSettings] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [telegramLink, setTelegramLink] = useState('');
  const [whatsappCommunityLink, setWhatsappCommunityLink] = useState('');
  const [savingLinks, setSavingLinks] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);

  useEffect(() => {
    fetchHeroBanner();
    fetchScheduleSettings();
    fetchSocialLinks();
  }, []);

  const fetchHeroBanner = async () => {
    try {
      const response = await api.get('/cms/hero-banner');
      setHeroBanner(response.data.hero_image || '');
      setNewBannerUrl(response.data.hero_image || '');
    } catch (error) {
      console.error('Error fetching hero banner:', error);
    }
  };

  const fetchScheduleSettings = async () => {
    setLoadingSchedule(true);
    try {
      const response = await api.get('/cms/schedule-settings');
      setScheduleSettings(response.data);
    } catch (error) {
      toast.error('Failed to fetch schedule settings');
    } finally {
      setLoadingSchedule(false);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const response = await api.get('/cms/social-links');
      setTelegramLink(response.data.telegram_link || '');
      setWhatsappCommunityLink(response.data.whatsapp_community_link || '');
    } catch (error) {
      console.error('Error fetching social links:', error);
    }
  };

  const saveSocialLinks = async () => {
    setSavingLinks(true);
    try {
      await api.put('/cms/social-links', {
        telegram_link: telegramLink,
        whatsapp_community_link: whatsappCommunityLink,
      });
      toast.success('Social links updated! These will be used in WhatsApp templates as variables {{3}} and {{4}}.');
    } catch (error) {
      toast.error('Failed to update social links');
    } finally {
      setSavingLinks(false);
    }
  };

  const runScan = async () => {
    setScanning(true);
    setMigrateResult(null);
    try {
      const r = await api.get('/cms/migration/scan');
      setScanResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const runMigration = async () => {
    if (!window.confirm('Migrate all locally-stored CMS images to Cloudflare R2? Images missing from this server cannot be recovered and will need re-upload.')) return;
    setMigrating(true);
    try {
      const r = await api.post('/cms/migration/run');
      setMigrateResult(r.data);
      toast.success(r.data?.message || 'Migration complete');
      // Refresh scan to show remaining items
      const s = await api.get('/cms/migration/scan');
      setScanResult(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  const handleBannerUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('page_type', 'home');
      formData.append('image_type', 'hero_banner');
      const response = await api.post('/cms/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Backend may return either a full R2 URL or a legacy relative path.
      const path = response.data.path || '';
      const fullUrl = /^https?:\/\//i.test(path) ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`;
      setNewBannerUrl(fullUrl);
      toast.success('Banner image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const saveBanner = async () => {
    if (!newBannerUrl) { toast.error('Please provide a banner image URL'); return; }
    setSaving(true);
    try {
      await api.put('/cms/hero-banner', { hero_image: newBannerUrl });
      setHeroBanner(newBannerUrl);
      toast.success('Hero banner updated!');
    } catch (error) {
      toast.error('Failed to update banner');
    } finally {
      setSaving(false);
    }
  };

  const toggleSchedule = async (key, currentEnabled) => {
    try {
      await api.put(`/cms/schedule-settings/${key}`, { enabled: !currentEnabled });
      setScheduleSettings(prev => prev.map(s => s.key === key ? { ...s, enabled: !currentEnabled } : s));
      toast.success(`Schedule ${!currentEnabled ? 'enabled' : 'paused'}`);
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const scheduleIcons = {
    'profile_incomplete_reminder': Bell,
    'weekly_job_alert': Briefcase,
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Site Settings</h1>
        <p className="text-slate-600 mt-1">Manage hero banner and scheduled tasks</p>
      </div>

      {/* Hero Banner Section */}
      <Card className="p-6 mb-6 border-slate-200">
        <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Image className="w-5 h-5 text-teal-600" />
          Hero Banner
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Preview */}
          <div>
            <p className="text-sm text-slate-600 mb-2">Current Banner:</p>
            <div className="aspect-video rounded-lg overflow-hidden bg-slate-100 border">
              {heroBanner ? (
                <img src={heroBanner} alt="Current Hero Banner" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <Image className="w-12 h-12" />
                </div>
              )}
            </div>
          </div>

          {/* New Banner */}
          <div>
            <p className="text-sm text-slate-600 mb-2">New Banner:</p>
            <div className="aspect-video rounded-lg overflow-hidden bg-slate-100 border mb-3">
              {newBannerUrl && newBannerUrl !== heroBanner ? (
                <img src={newBannerUrl} alt="New Banner Preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <p className="text-sm">Upload or paste URL</p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <Input
                data-testid="banner-url-input"
                value={newBannerUrl}
                onChange={(e) => setNewBannerUrl(e.target.value)}
                placeholder="Paste image URL here..."
              />

              <div className="flex gap-2">
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg cursor-pointer text-sm transition-colors">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload Image'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleBannerUpload(e.target.files[0])} disabled={uploading} />
                </label>
                <Button data-testid="save-banner-btn" onClick={saveBanner} disabled={saving || !newBannerUrl}>
                  {saving ? 'Saving...' : 'Save Banner'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* CMS Storage Sync (Local → Cloudflare R2) */}
      <Card className="p-6 mb-6 border-amber-200 bg-amber-50/40">
        <h2 className="text-xl font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <Cloud className="w-5 h-5 text-amber-600" />
          CMS Image Storage Sync
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          New CMS uploads now go to Cloudflare R2 automatically. Use this tool to find and migrate any
          old images still hosted on the server disk (those break on redeploy).
        </p>

        <div className="flex flex-wrap gap-3 mb-4">
          <Button
            data-testid="cms-sync-scan-btn"
            variant="outline"
            onClick={runScan}
            disabled={scanning}
          >
            {scanning ? 'Scanning…' : 'Scan for Local Images'}
          </Button>
          {scanResult && (
            <Button
              data-testid="cms-sync-migrate-btn"
              onClick={runMigration}
              disabled={migrating || scanResult.migratable === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              title={scanResult.migratable === 0 ? 'No local files found to migrate' : ''}
            >
              <Cloud className="w-4 h-4 mr-1.5" />
              {migrating
                ? 'Migrating…'
                : scanResult.migratable > 0
                  ? `Migrate ${scanResult.migratable} Image(s) to R2`
                  : 'Nothing to Migrate'}
            </Button>
          )}
        </div>

        {scanResult && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white border rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-slate-900">{scanResult.total}</div>
                <div className="text-xs text-slate-500 mt-1">Total local refs in DB</div>
              </div>
              <div className="bg-white border rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-emerald-700 flex items-center justify-center gap-1">
                  <CheckCircle2 className="w-5 h-5" /> {scanResult.migratable}
                </div>
                <div className="text-xs text-slate-500 mt-1">Ready to migrate</div>
              </div>
              <div className="bg-white border rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-rose-600 flex items-center justify-center gap-1">
                  <AlertCircle className="w-5 h-5" /> {scanResult.missing_local_file}
                </div>
                <div className="text-xs text-slate-500 mt-1">Missing on disk (need re-upload)</div>
              </div>
            </div>

            {scanResult.total > 0 && (
              <details className="bg-white border rounded-lg">
                <summary className="cursor-pointer px-4 py-2 text-sm font-medium text-slate-700">
                  View details ({scanResult.items.length} items shown)
                </summary>
                <div className="px-4 pb-3 max-h-60 overflow-y-auto text-xs font-mono">
                  {scanResult.items.map((it, i) => (
                    <div key={i} className="border-t py-1 flex items-start gap-2">
                      {it.local_exists ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0" />
                      ) : (
                        <AlertCircle className="w-3 h-3 text-rose-500 mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1 truncate">
                        <span className="text-slate-500">[{it.collection}/{it.doc_id}]</span> {it.url}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {scanResult.total === 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                All CMS images are already on Cloudflare R2 — nothing to migrate.
              </div>
            )}
          </div>
        )}

        {migrateResult && (
          <div className="mt-4 p-3 rounded-lg border bg-white text-sm">
            <p className="font-medium text-slate-800">{migrateResult.message}</p>
            {migrateResult.failed_count > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-rose-600">Show {migrateResult.failed_count} failed item(s)</summary>
                <ul className="text-xs text-slate-600 mt-1 ml-4 list-disc">
                  {migrateResult.failed.slice(0, 50).map((f, i) => (
                    <li key={i} className="truncate">[{f.collection}] {f.url} — {f.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </Card>

      {/* Social / Community Links Section */}
      <Card className="p-6 mb-6 border-slate-200">
        <h2 className="text-xl font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-teal-600" />
          Community Links
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          These links are passed as variables <code className="px-1 bg-slate-100 rounded text-xs">{'{{3}}'}</code> (Telegram) and{' '}
          <code className="px-1 bg-slate-100 rounded text-xs">{'{{4}}'}</code> (WhatsApp Community) into WhatsApp templates such as the Weekly Job Alert.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-2">
              <Send className="w-4 h-4 text-sky-500" /> Telegram Link
            </label>
            <Input
              data-testid="telegram-link-input"
              value={telegramLink}
              onChange={(e) => setTelegramLink(e.target.value)}
              placeholder="https://t.me/your_channel"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-emerald-500" /> WhatsApp Community Link
            </label>
            <Input
              data-testid="whatsapp-community-link-input"
              value={whatsappCommunityLink}
              onChange={(e) => setWhatsappCommunityLink(e.target.value)}
              placeholder="https://chat.whatsapp.com/your_invite"
            />
          </div>
        </div>

        <div className="mt-4">
          <Button
            data-testid="save-social-links-btn"
            onClick={saveSocialLinks}
            disabled={savingLinks}
          >
            {savingLinks ? 'Saving...' : 'Save Community Links'}
          </Button>
        </div>
      </Card>

      {/* Schedule Settings Section */}
      <Card className="p-6 border-slate-200">
        <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-teal-600" />
          Scheduled Tasks
        </h2>
        <p className="text-sm text-slate-600 mb-4">Enable or disable scheduled background tasks. Changes take effect on the next cycle.</p>

        {loadingSchedule ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {scheduleSettings.map((setting) => {
              const IconComp = scheduleIcons[setting.key] || Clock;
              return (
                <div key={setting.key} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${setting.enabled ? 'bg-teal-100' : 'bg-slate-200'}`}>
                      <IconComp className={`w-5 h-5 ${setting.enabled ? 'text-teal-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{setting.name}</p>
                      <p className="text-xs text-slate-500">
                        {setting.key === 'weekly_job_alert' ? 'Runs every 15 minutes, sends weekly matched jobs to eligible seekers' :
                         setting.key === 'profile_incomplete_reminder' ? 'Runs hourly, reminds job seekers to complete their profile' :
                         setting.key}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${setting.enabled ? 'text-teal-600' : 'text-slate-400'}`}>
                      {setting.enabled ? 'Active' : 'Paused'}
                    </span>
                    <Switch
                      data-testid={`schedule-toggle-${setting.key}`}
                      checked={setting.enabled}
                      onCheckedChange={() => toggleSchedule(setting.key, setting.enabled)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SiteSettingsCMS;
